import React, { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Tooltip,
  useMediaQuery,
} from '@mui/material'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import RemoveIcon from '@mui/icons-material/Remove'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { CSVDownload } from 'react-csv'

// ─── Types ───────────────────────────────────────────────────────────────────

interface LeaderboardRow {
  rank: number
  alertName: string
  symbol: string
  alertShareableId: string | null
  patternName: string
  patternDescription: string
  patternShareableId: string | null
  daysInTopTen: number
  todayPosition: number
  yesterdayPosition: number | null
  profitPercent: number
  winningTrades: number
  losingTrades: number
  winRate: number
  tradeRatio: { risk: number; reward: number }
  riskPerTrade: number
  firstTradeDate: string
  lastTradeDate: string
  totalTrades: number
  alertId: string
  tradingHouse: string
  accountType: 'live' | 'demo'
}

interface LeaderboardResponse {
  rows: LeaderboardRow[]
  generatedAt: string
  leaderboardVersion: string
}

// ─── Constants ───────────────────────────────────────────────────────────────

const isProd = process.env.REACT_APP_ENVIRONMENT === 'production'

const LEADERBOARD_URL = isProd
  ? 'https://us-central1-greenchart-93a2a.cloudfunctions.net/getLeaderboard'
  : 'https://us-central1-greenchart-dev.cloudfunctions.net/getLeaderboard'

const LEADERBOARD_TRADES_URL = isProd
  ? 'https://us-central1-greenchart-93a2a.cloudfunctions.net/getLeaderboardTrades'
  : 'https://us-central1-greenchart-dev.cloudfunctions.net/getLeaderboardTrades'

const RANK_ACCENTS = ['#FFD700', '#C0C0C0', '#CD7F32']
const MEDALS = ['🥇', '🥈', '🥉']

// ─── Helpers ─────────────────────────────────────────────────────────────────

function timeAgo(iso: string): string {
  if (!iso) return ''
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000)
  if (s < 60) return 'just now'
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

function formatDate(iso: string): string {
  if (!iso || iso === 'Unknown') return '—'
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  } catch {
    return '—'
  }
}

function shareableUrl(id: string) {
  return `https://members.daytradeu.com/trading/strategy-suite?shareable=${id}`
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function PositionDelta({ current, previous }: { current: number; previous: number | null }) {
  if (previous === null) {
    return (
      <Typography
        sx={{
          fontSize: '0.55rem',
          fontWeight: 700,
          color: 'rgba(255,255,255,0.3)',
          letterSpacing: '0.04em',
          textAlign: 'center',
        }}
      >
        NEW
      </Typography>
    )
  }
  const diff = previous - current
  if (diff > 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.125 }}>
        <ArrowUpwardIcon sx={{ fontSize: 9, color: '#00ff5c' }} />
        <Typography
          sx={{
            fontSize: '0.55rem',
            fontWeight: 700,
            color: '#00ff5c',
            fontFamily: "'JetBrains Mono'",
          }}
        >
          {diff}
        </Typography>
      </Box>
    )
  }
  if (diff < 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.125 }}>
        <ArrowDownwardIcon sx={{ fontSize: 9, color: '#f44336' }} />
        <Typography
          sx={{
            fontSize: '0.55rem',
            fontWeight: 700,
            color: '#f44336',
            fontFamily: "'JetBrains Mono'",
          }}
        >
          {Math.abs(diff)}
        </Typography>
      </Box>
    )
  }
  return (
    <RemoveIcon
      sx={{ fontSize: 9, color: 'rgba(255,255,255,0.2)', display: 'block', mx: 'auto' }}
    />
  )
}

function RankBadge({
  rank,
  current,
  previous,
}: {
  rank: number
  current: number
  previous: number | null
}) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.375 }}>
      {rank <= 3 ? (
        <Box
          sx={{
            fontSize: '1.125rem',
            lineHeight: 1,
            filter: `drop-shadow(0 0 5px ${RANK_ACCENTS[rank - 1]}99)`,
          }}
        >
          {MEDALS[rank - 1]}
        </Box>
      ) : (
        <Typography
          sx={{
            fontFamily: "'JetBrains Mono', monospace",
            fontWeight: 700,
            fontSize: '0.8rem',
            color: 'rgba(255,255,255,0.35)',
            lineHeight: 1,
          }}
        >
          {rank}
        </Typography>
      )}
      <PositionDelta current={current} previous={previous} />
    </Box>
  )
}

/**
 * Color the win rate relative to the break-even win rate for the plan's R/R.
 *   breakEven = risk / (risk + reward)  e.g. 1:4 → 20%, 1:1 → 50%
 *   winRate well above breakEven → green
 *   winRate near breakEven      → yellow
 *   winRate below breakEven     → red
 */
function WinRateBar({
  winRate,
  tradeRatio,
}: {
  winRate: number
  tradeRatio?: { risk: number; reward: number } | null
}) {
  const total = (tradeRatio?.risk ?? 0) + (tradeRatio?.reward ?? 0)
  const breakEven = total > 0 ? (tradeRatio!.risk / total) * 100 : null

  let color: string
  if (breakEven !== null) {
    const surplus = winRate - breakEven
    color = surplus > 5 ? '#00ff5c' : surplus > -5 ? '#FFD700' : '#f44336'
  } else {
    color = winRate >= 60 ? '#00ff5c' : winRate >= 45 ? '#FFD700' : '#f44336'
  }

  const hasRatio = (tradeRatio?.risk ?? 0) > 0 && (tradeRatio?.reward ?? 0) > 0

  return (
    <Box sx={{ minWidth: 64 }}>
      <Typography
        sx={{
          fontFamily: "'JetBrains Mono', monospace",
          fontWeight: 600,
          fontSize: '0.78rem',
          color,
          lineHeight: 1,
        }}
      >
        {winRate.toFixed(1)}%
      </Typography>
      <Box
        sx={{
          position: 'relative',
          height: 2,
          bgcolor: 'rgba(255,255,255,0.07)',
          borderRadius: 99,
          mt: 0.5,
        }}
      >
        <Box
          sx={{
            height: '100%',
            width: `${Math.min(winRate, 100)}%`,
            bgcolor: color,
            borderRadius: 99,
            transition: 'width 0.6s ease',
          }}
        />
        {breakEven !== null && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: `${breakEven}%`,
              transform: 'translate(-50%, -50%)',
              width: 1.5,
              height: 6,
              bgcolor: 'rgba(255,255,255,0.9)',
              borderRadius: 1,
            }}
          />
        )}
      </Box>
      {hasRatio && (
        <Typography
          sx={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: '0.6rem',
            color: 'rgba(255,255,255,0.6)',
            mt: 0.5,
            lineHeight: 1,
          }}
        >
          ratio {tradeRatio!.risk}:{tradeRatio!.reward}
        </Typography>
      )}
    </Box>
  )
}

// ─── Trades download ─────────────────────────────────────────────────────────

interface Trade {
  _id: string
  status: string
  reason?: string
  type: 'buy' | 'sell'
  symbol: string
  entryTime: number
  entryPrice: number
  exitTime?: number
  exitPrice?: number
  sl: number
  tp: number
  lotSize: number
  openEquity: number
  commission: number
  profit?: number
  positionId?: number
  closeNotificationId?: string
  errorSource?: string
  errorCode?: number
  errorMessage?: string
}

interface CashFlowDetail {
  amount: number
  date: string // ISO date string
  type: 'DEPOSIT' | 'WITHDRAWAL'
}

async function fetchLeaderboardTrades(
  alertId: string,
  firstTradeDate?: string
): Promise<{ trades: Trade[]; cashFlows: CashFlowDetail[] }> {
  let url = `${LEADERBOARD_TRADES_URL}?alertId=${encodeURIComponent(alertId)}`
  if (firstTradeDate) url += `&firstTradeDate=${encodeURIComponent(firstTradeDate)}`
  const res = await fetch(url)
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  const json: { data: Trade[]; count: number; cashFlows?: CashFlowDetail[] } = await res.json()
  return { trades: json.data || [], cashFlows: json.cashFlows || [] }
}

function formatTradeDate(ts: number): string {
  if (!ts) return ''
  return new Date(ts).toLocaleString()
}

function getTradeDuration(entryTime: number, exitTime?: number): string {
  if (!exitTime || !entryTime) return ''
  let secs = Math.floor((exitTime - entryTime) / 1000)
  let out = ''
  if (secs >= 3600) {
    out += `${Math.floor(secs / 3600)}hr`
    secs %= 3600
  }
  if (secs >= 60) {
    out += ` ${Math.floor(secs / 60)}m`
    secs %= 60
  }
  out += ` ${secs}s`
  return out.trim()
}

function getExitType(trade: Trade): string {
  if (trade.status === 'REJECTED') return trade.reason || 'REJECTED'
  if (trade.status === 'PENDING') return 'PENDING'
  if (trade.status !== 'CLOSED' && trade.status !== 'closed') return 'OPEN'
  if (trade.exitPrice === undefined) return 'Unknown'
  if (trade.closeNotificationId) return 'Exit Pattern'
  const epsilon = Math.max(1e-4, Math.abs(trade.entryPrice) * 1e-6)
  const slPrice = trade.type === 'buy' ? trade.entryPrice - trade.sl : trade.entryPrice + trade.sl
  const tpPrice = trade.type === 'buy' ? trade.entryPrice + trade.tp : trade.entryPrice - trade.tp
  if (trade.type === 'buy') {
    if (trade.exitPrice >= tpPrice - epsilon) return 'Take Profit'
    if (trade.exitPrice <= slPrice + epsilon) return 'Stop Loss'
  } else {
    if (trade.exitPrice <= tpPrice + epsilon) return 'Take Profit'
    if (trade.exitPrice >= slPrice - epsilon) return 'Stop Loss'
  }
  return 'Manual'
}

function buildTradesCsv(row: LeaderboardRow, trades: Trade[], cashFlows: CashFlowDetail[]): any[] {
  const data: any[] = []
  data.push(['Auto-Trade Leaderboard — Individual Trades'])
  data.push(['Generated:', new Date().toLocaleString()])
  data.push([])
  data.push(['Alert Name:', row.alertName])
  data.push(['Pattern Name:', row.patternName])
  data.push(['Symbol:', row.symbol])
  data.push(['Profit %:', `${row.profitPercent.toFixed(2)}%`])
  data.push(['Win Rate:', `${row.winRate.toFixed(1)}%`])
  data.push(['Total Trades:', row.totalTrades])
  data.push([])
  data.push([
    'Entry Date',
    'Exit Date',
    'Duration',
    'Status',
    'Exit Type',
    'Type',
    'Symbol',
    'Entry Price',
    'Exit Price',
    'Stop Loss',
    'Take Profit',
    'Lot Size',
    'Open Equity',
    'Commission',
    'Profit',
    'Running P&L',
    'Position ID',
    'Error Source',
    'Error Code',
    'Error Message',
  ])

  type TradeEvent = { kind: 'trade'; time: number; trade: Trade }
  type CashFlowEvent = { kind: 'cashflow'; time: number; cf: CashFlowDetail }
  const events: (TradeEvent | CashFlowEvent)[] = [
    ...trades.map((t) => ({ kind: 'trade' as const, time: t.entryTime, trade: t })),
    ...cashFlows.map((cf) => ({
      kind: 'cashflow' as const,
      time: new Date(cf.date).getTime(),
      cf,
    })),
  ]
  events.sort((a, b) => a.time - b.time)

  let runningPL = 0
  let runningEquity = 0
  for (const event of events) {
    if (event.kind === 'trade') {
      const t = event.trade
      const closed = t.status === 'CLOSED' || t.status === 'closed'
      if (closed && t.profit) {
        runningPL += t.profit
        runningEquity += t.profit
      }
      if (t.openEquity) runningEquity = t.openEquity
      data.push([
        formatTradeDate(t.entryTime),
        closed ? formatTradeDate(t.exitTime || 0) : 'Open Trade',
        closed ? getTradeDuration(t.entryTime, t.exitTime) : '',
        t.status,
        getExitType(t),
        t.type.toUpperCase(),
        t.symbol,
        t.entryPrice?.toFixed(5) || '',
        t.exitPrice?.toFixed(5) || '',
        t.sl?.toFixed(5) || '',
        t.tp?.toFixed(5) || '',
        t.lotSize?.toFixed(2) || '',
        t.openEquity ? `$${t.openEquity.toFixed(2)}` : '',
        t.commission ? `$${t.commission.toFixed(2)}` : '$0.00',
        closed && t.profit !== undefined ? `$${t.profit.toFixed(2)}` : '',
        closed ? `$${runningPL.toFixed(2)}` : '',
        t.positionId || '',
        t.errorSource || '',
        t.errorCode || '',
        t.errorMessage || '',
      ])
    } else {
      const cf = event.cf
      runningEquity += cf.amount
      data.push([
        formatTradeDate(event.time),
        '',
        '', // Exit Date, Duration
        cf.type,
        '',
        '',
        '', // Exit Type, Type, Symbol
        '',
        '',
        '',
        '',
        '', // Entry Price, Exit Price, SL, TP, Lot Size
        `$${runningEquity.toFixed(2)}`,
        '', // Commission
        `$${cf.amount.toFixed(2)}`,
        `$${runningPL.toFixed(2)}`,
        '',
        '',
        '',
        '', // Position ID, Error Source, Error Code, Error Message
      ])
    }
  }
  return data
}

function TradesDownloadButton({
  row,
  onAction,
}: {
  row: LeaderboardRow
  onAction: (e: React.MouseEvent) => void
}) {
  const [loading, setLoading] = useState(false)
  const [csvData, setCsvData] = useState<any[]>([])
  const [ready, setReady] = useState(false)
  const [error, setError] = useState(false)

  useEffect(() => {
    if (ready) setReady(false)
  }, [ready])

  const handleClick = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation()
      if (loading || !row.alertId || row.totalTrades === 0) return
      setLoading(true)
      setError(false)
      try {
        const { trades, cashFlows } = await fetchLeaderboardTrades(
          row.alertId,
          row.firstTradeDate !== 'Unknown' ? row.firstTradeDate : undefined
        )
        if (trades.length > 0) {
          setCsvData(buildTradesCsv(row, trades, cashFlows))
          setReady(true)
        }
      } catch {
        setError(true)
      } finally {
        setLoading(false)
      }
    },
    [row, loading]
  )

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        startIcon={
          loading ? (
            <CircularProgress size={12} sx={{ color: 'inherit' }} />
          ) : (
            <FileDownloadIcon sx={{ fontSize: '14px !important' }} />
          )
        }
        onClick={handleClick}
        disabled={loading || row.totalTrades === 0}
        sx={{
          fontSize: '0.7rem',
          fontWeight: 600,
          py: 0.375,
          px: 1.25,
          color: error ? '#f44336' : 'rgba(255,255,255,0.45)',
          borderColor: error ? 'rgba(244,67,54,0.4)' : 'rgba(255,255,255,0.12)',
          bgcolor: 'transparent',
          '&:hover': {
            borderColor: 'rgba(255,255,255,0.3)',
            bgcolor: 'rgba(255,255,255,0.04)',
            color: 'rgba(255,255,255,0.75)',
          },
          '&.Mui-disabled': { opacity: 0.4 },
        }}
      >
        {loading ? 'Loading…' : error ? 'Failed — retry' : 'Download Trades'}
      </Button>
      {ready && (
        <CSVDownload data={csvData} filename={`${row.alertName}-trades.csv`} target="_blank" />
      )}
    </>
  )
}

function StatBlock({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box>
      <Typography
        sx={{
          fontSize: '0.58rem',
          fontWeight: 700,
          color: 'rgba(255,255,255,0.28)',
          letterSpacing: '0.07em',
          textTransform: 'uppercase',
          mb: 0.375,
        }}
      >
        {label}
      </Typography>
      {children}
    </Box>
  )
}

function StatText({ children, color }: { children: React.ReactNode; color?: string }) {
  return (
    <Typography
      sx={{
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: '0.78rem',
        fontWeight: 600,
        color: color ?? 'rgba(255,255,255,0.7)',
        lineHeight: 1.2,
      }}
    >
      {children}
    </Typography>
  )
}

function ExpandedDetail({
  row,
  onAction,
  showWinRate,
}: {
  row: LeaderboardRow
  onAction: (e: React.MouseEvent) => void
  showWinRate?: boolean
}) {
  return (
    <Box
      sx={{
        bgcolor: 'rgba(0,0,0,0.18)',
        borderTop: '1px solid rgba(255,255,255,0.04)',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        px: 2.5,
        pt: 1.5,
        pb: 1.25,
      }}
    >
      {/* Stat strip */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 1.5 }}>
        {showWinRate && (
          <StatBlock label="Win Rate">
            <WinRateBar winRate={row.winRate} tradeRatio={row.tradeRatio} />
          </StatBlock>
        )}
        <StatBlock label="Days in Top 10">
          <StatText color={row.daysInTopTen > 30 ? '#FFD700' : undefined}>
            {row.daysInTopTen}d
          </StatText>
        </StatBlock>

        <StatBlock label="W / L / Total">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.375 }}>
            <Typography
              sx={{
                fontFamily: "'JetBrains Mono'",
                fontSize: '0.78rem',
                fontWeight: 700,
                color: '#00ff5c',
              }}
            >
              {row.winningTrades}
            </Typography>
            <Typography sx={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.2)' }}>/</Typography>
            <Typography
              sx={{
                fontFamily: "'JetBrains Mono'",
                fontSize: '0.78rem',
                fontWeight: 700,
                color: '#f44336',
              }}
            >
              {row.losingTrades}
            </Typography>
            <Typography sx={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.2)' }}>/</Typography>
            <Typography
              sx={{
                fontFamily: "'JetBrains Mono'",
                fontSize: '0.78rem',
                fontWeight: 600,
                color: 'rgba(255,255,255,0.5)',
              }}
            >
              {row.totalTrades}
            </Typography>
          </Box>
        </StatBlock>

        <StatBlock label="First trade">
          <StatText color="rgba(255,255,255,0.5)">{formatDate(row.firstTradeDate)}</StatText>
        </StatBlock>

        <StatBlock label="Last trade">
          <StatText color="rgba(255,255,255,0.5)">{formatDate(row.lastTradeDate)}</StatText>
        </StatBlock>

        <StatBlock label="Risk / Trade">
          <StatText>{(row.riskPerTrade * 100).toFixed(1)}%</StatText>
        </StatBlock>
      </Box>

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }} onClick={onAction}>
        {row.alertShareableId && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddCircleOutlineIcon sx={{ fontSize: '14px !important' }} />}
            onClick={() => window.open(shareableUrl(row.alertShareableId!), '_blank')}
            sx={{
              fontSize: '0.7rem',
              fontWeight: 600,
              py: 0.375,
              px: 1.25,
              color: '#1DC30E',
              borderColor: 'rgba(29,195,14,0.3)',
              bgcolor: 'rgba(29,195,14,0.05)',
              '&:hover': { borderColor: '#1DC30E', bgcolor: 'rgba(29,195,14,0.12)' },
            }}
          >
            Add Alert
          </Button>
        )}
        {row.patternShareableId && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddCircleOutlineIcon sx={{ fontSize: '14px !important' }} />}
            onClick={() => window.open(shareableUrl(row.patternShareableId!), '_blank')}
            sx={{
              fontSize: '0.7rem',
              fontWeight: 600,
              py: 0.375,
              px: 1.25,
              color: '#00a1ff',
              borderColor: 'rgba(0,161,255,0.3)',
              bgcolor: 'rgba(0,161,255,0.05)',
              '&:hover': { borderColor: '#00a1ff', bgcolor: 'rgba(0,161,255,0.12)' },
            }}
          >
            Add Pattern
          </Button>
        )}
        <TradesDownloadButton row={row} onAction={onAction} />
      </Box>
    </Box>
  )
}

// ─── Cell sx ─────────────────────────────────────────────────────────────────

const headerCellSx = {
  bgcolor: '#16171A',
  color: 'rgba(255,255,255,0.35)',
  fontSize: '0.6rem',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase' as const,
  borderBottom: '1px solid rgba(255,255,255,0.06)',
  whiteSpace: 'nowrap' as const,
  py: 1.25,
  px: 1.75,
}

const bodyCellSx = {
  borderBottom: '1px solid rgba(255,255,255,0.04)',
  py: 1.125,
  px: 1.75,
  verticalAlign: 'middle',
}

const stickyRankHeaderSx = {
  ...headerCellSx,
  position: 'sticky' as const,
  left: 0,
  zIndex: 4,
  width: 52,
  textAlign: 'center' as const,
}

const stickyAlertHeaderSx = {
  ...headerCellSx,
  position: 'sticky' as const,
  left: 52,
  zIndex: 4,
  minWidth: 220,
}

const stickyRankCellSx = {
  ...bodyCellSx,
  position: 'sticky' as const,
  left: 0,
  zIndex: 2,
  width: 52,
  bgcolor: '#1A1B1E',
  textAlign: 'center' as const,
}

const stickyAlertCellSx = {
  ...bodyCellSx,
  position: 'sticky' as const,
  left: 52,
  zIndex: 2,
  minWidth: 220,
  bgcolor: '#1A1B1E',
}

// ─── Skeleton rows ────────────────────────────────────────────────────────────

function SkeletonRows() {
  const collapsePattern = useMediaQuery('(max-width:640px)')
  const collapseWinRate = useMediaQuery('(max-width:480px)')
  return (
    <>
      {Array.from({ length: 10 }).map((_, i) => (
        <TableRow key={i}>
          <TableCell sx={stickyRankCellSx}>
            <Skeleton variant="circular" width={24} height={24} sx={{ mx: 'auto', mb: 0.5 }} />
            <Skeleton width={20} height={10} sx={{ mx: 'auto' }} />
          </TableCell>
          <TableCell sx={{ ...stickyAlertCellSx, bgcolor: '#1A1B1E' }}>
            <Skeleton width="75%" height={16} />
            <Skeleton width="35%" height={12} sx={{ mt: 0.5 }} />
          </TableCell>
          {!collapsePattern && (
            <TableCell sx={bodyCellSx}>
              <Skeleton width={i % 2 === 0 ? '80%' : '60%'} height={16} />
            </TableCell>
          )}
          <TableCell sx={bodyCellSx}>
            <Skeleton width={60} height={16} />
          </TableCell>
          {!collapseWinRate && (
            <TableCell sx={bodyCellSx}>
              <Skeleton width={56} height={16} />
            </TableCell>
          )}
        </TableRow>
      ))}
    </>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function AutoTradeLeaderboard() {
  const [data, setData] = useState<LeaderboardResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expandedRow, setExpandedRow] = useState<string | null>(null)

  // Below 640px the Pattern column is hidden; content moves into the Alert cell.
  const collapsePattern = useMediaQuery('(max-width:640px)')
  // Below 480px the Win Rate column is also hidden; content moves into the expanded detail.
  const collapseWinRate = useMediaQuery('(max-width:480px)')

  const fetchLeaderboard = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await fetch(LEADERBOARD_URL)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const json: LeaderboardResponse = await res.json()
      setData(json)
    } catch (e: any) {
      setError(e.message ?? 'Failed to load leaderboard')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchLeaderboard()
  }, [fetchLeaderboard])

  const handleRowClick = (alertId: string) => {
    setExpandedRow((prev) => (prev === alertId ? null : alertId))
  }

  const stopPropagation = (e: React.MouseEvent) => e.stopPropagation()

  const rows = (data?.rows ?? []).filter((r) => r.accountType === 'live')

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#1A1B1E',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: "'Plus Jakarta Sans Variable', 'Plus Jakarta Sans', sans-serif",
      }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          px: 2.5,
          pt: 2,
          pb: 1.5,
          background: 'linear-gradient(180deg, rgba(29,195,14,0.05) 0%, transparent 100%)',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box
            sx={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              bgcolor: '#1DC30E',
              boxShadow: '0 0 7px #1DC30E',
              flexShrink: 0,
              animation: !loading && !error ? 'pulse 2s ease-in-out infinite' : 'none',
              '@keyframes pulse': {
                '0%, 100%': { opacity: 1, boxShadow: '0 0 7px #1DC30E' },
                '50%': { opacity: 0.4, boxShadow: '0 0 3px #1DC30E' },
              },
            }}
          />
          <Box>
            <Typography
              sx={{
                fontSize: '0.875rem',
                fontWeight: 700,
                letterSpacing: '-0.02em',
                color: 'rgba(255,255,255,0.9)',
                lineHeight: 1,
              }}
            >
              Auto-Trade Leaderboard
            </Typography>
            <Typography sx={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.3)', mt: 0.375 }}>
              Top 10 live alerts by profit % &middot; click any row for details
              {data?.generatedAt && <> &middot; updated {timeAgo(data.generatedAt)}</>}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* ── Body ── */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {error ? (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              py: 8,
              color: 'rgba(255,255,255,0.4)',
            }}
          >
            <ErrorOutlineIcon sx={{ fontSize: 36, color: '#f44336', opacity: 0.7 }} />
            <Typography sx={{ fontSize: '0.825rem' }}>Unable to load leaderboard</Typography>
            <Box
              component="button"
              onClick={fetchLeaderboard}
              sx={{
                px: 2,
                py: 0.75,
                bgcolor: 'rgba(29,195,14,0.1)',
                color: '#1DC30E',
                border: '1px solid rgba(29,195,14,0.3)',
                borderRadius: 1,
                fontSize: '0.75rem',
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'inherit',
                '&:hover': { bgcolor: 'rgba(29,195,14,0.16)' },
              }}
            >
              Try again
            </Box>
          </Box>
        ) : (
          <TableContainer sx={{ overflowX: 'auto', flex: 1 }}>
            <Table
              size="small"
              stickyHeader
              sx={{
                tableLayout: 'auto',
                minWidth: collapseWinRate ? 300 : collapsePattern ? 380 : 560,
                '& .MuiTableRow-root.data-row': { cursor: 'pointer' },
                '& .MuiTableRow-root.data-row:hover td': { bgcolor: '#212226' },
                '& .MuiTableRow-root.data-row.expanded td': {
                  borderBottom: 0,
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={stickyRankHeaderSx}>#</TableCell>
                  <TableCell sx={{ ...stickyAlertHeaderSx, minWidth: collapsePattern ? 140 : 220 }}>
                    Alert
                  </TableCell>
                  {!collapsePattern && (
                    <TableCell sx={{ ...headerCellSx, minWidth: 140 }}>Pattern</TableCell>
                  )}
                  <TableCell
                    sx={{
                      ...headerCellSx,
                      minWidth: collapsePattern ? 70 : 90,
                      textAlign: 'right',
                    }}
                  >
                    Profit %
                  </TableCell>
                  {!collapseWinRate && (
                    <TableCell sx={{ ...headerCellSx, minWidth: collapsePattern ? 64 : 80 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        Win Rate
                        <Tooltip
                          title="Win rate colored relative to the break-even threshold for each alert's trade ratio. Green = profitable above break-even, yellow = near break-even, red = below. The bar marker shows where break-even falls."
                          placement="top"
                          arrow
                        >
                          <InfoOutlinedIcon
                            sx={{
                              fontSize: 11,
                              color: 'rgba(255,255,255,0.25)',
                              cursor: 'default',
                              flexShrink: 0,
                            }}
                          />
                        </Tooltip>
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <SkeletonRows />
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={collapseWinRate ? 3 : collapsePattern ? 4 : 5}
                      sx={{ textAlign: 'center', py: 8, color: 'rgba(255,255,255,0.3)', border: 0 }}
                    >
                      No leaderboard data available yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => {
                    const accentColor = row.rank <= 3 ? RANK_ACCENTS[row.rank - 1] : null
                    const isExpanded = expandedRow === row.alertId

                    return (
                      <React.Fragment key={row.alertId}>
                        {/* ── Data row ── */}
                        <TableRow
                          className={`data-row${isExpanded ? ' expanded' : ''}`}
                          onClick={() => handleRowClick(row.alertId)}
                          sx={{
                            transition: 'background-color 0.12s',
                            ...(isExpanded && {
                              bgcolor: '#212226',
                              '& td': { bgcolor: '#212226 !important' },
                            }),
                            ...(accentColor && {
                              '& td:first-of-type': { boxShadow: `inset 3px 0 0 ${accentColor}` },
                            }),
                          }}
                        >
                          {/* Rank */}
                          <TableCell
                            sx={{
                              ...stickyRankCellSx,
                              ...(isExpanded && { bgcolor: '#212226 !important' }),
                            }}
                          >
                            <RankBadge
                              rank={row.rank}
                              current={row.todayPosition}
                              previous={row.yesterdayPosition}
                            />
                          </TableCell>

                          {/* Alert */}
                          <TableCell
                            sx={{
                              ...stickyAlertCellSx,
                              minWidth: collapsePattern ? 140 : 220,
                              ...(isExpanded && { bgcolor: '#212226 !important' }),
                            }}
                          >
                            <Typography
                              sx={{
                                fontWeight: 600,
                                fontSize: '0.8rem',
                                color: 'rgba(255,255,255,0.9)',
                                whiteSpace: 'nowrap',
                                lineHeight: 1.2,
                              }}
                            >
                              {row.alertName}
                            </Typography>
                            {collapsePattern && row.patternName && (
                              <Typography
                                sx={{
                                  fontSize: '0.66rem',
                                  color: 'rgba(255,255,255,0.6)',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  maxWidth: 200,
                                  mt: 0.25,
                                  lineHeight: 1.2,
                                }}
                              >
                                {row.patternName}
                              </Typography>
                            )}
                            {row.symbol && (
                              <Box
                                sx={{
                                  display: 'inline-block',
                                  px: 0.625,
                                  py: 0.1,
                                  borderRadius: 0.5,
                                  bgcolor: 'rgba(0,161,255,0.1)',
                                  border: '1px solid rgba(0,161,255,0.2)',
                                  mt: 0.375,
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: '0.6rem',
                                    fontWeight: 700,
                                    fontFamily: "'JetBrains Mono', monospace",
                                    color: '#00a1ff',
                                    letterSpacing: '0.04em',
                                    lineHeight: 1,
                                  }}
                                >
                                  {row.symbol}
                                </Typography>
                              </Box>
                            )}
                          </TableCell>

                          {/* Pattern — hidden on small screens, content moves into Alert cell */}
                          {!collapsePattern && (
                            <TableCell sx={{ ...bodyCellSx, minWidth: 140 }}>
                              <Typography
                                sx={{
                                  fontSize: '0.775rem',
                                  color: 'rgba(255,255,255,0.6)',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  maxWidth: 180,
                                }}
                              >
                                {row.patternName || '—'}
                              </Typography>
                              {row.patternDescription && (
                                <Typography
                                  sx={{
                                    fontSize: '0.66rem',
                                    color: 'rgba(255,255,255,0.28)',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    maxWidth: 180,
                                    mt: 0.25,
                                  }}
                                >
                                  {row.patternDescription}
                                </Typography>
                              )}
                            </TableCell>
                          )}

                          {/* Profit % */}
                          <TableCell sx={{ ...bodyCellSx, minWidth: 90, textAlign: 'right' }}>
                            <Typography
                              sx={{
                                fontFamily: "'JetBrains Mono', monospace",
                                fontWeight: 700,
                                fontSize: '0.875rem',
                                letterSpacing: '-0.02em',
                                color: row.profitPercent >= 0 ? '#00ff5c' : '#f44336',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {row.profitPercent >= 0 ? '+' : ''}
                              {row.profitPercent.toFixed(2)}%
                            </Typography>
                          </TableCell>

                          {/* Win Rate */}
                          {!collapseWinRate && (
                            <TableCell sx={{ ...bodyCellSx, minWidth: 80 }}>
                              <WinRateBar winRate={row.winRate} tradeRatio={row.tradeRatio} />
                            </TableCell>
                          )}
                        </TableRow>

                        {/* ── Expanded detail row ── */}
                        <TableRow>
                          <TableCell
                            colSpan={collapseWinRate ? 3 : collapsePattern ? 4 : 5}
                            sx={{ p: 0, border: 0 }}
                          >
                            <Collapse in={isExpanded} timeout={200} unmountOnExit>
                              <ExpandedDetail
                                row={row}
                                onAction={stopPropagation}
                                showWinRate={collapseWinRate}
                              />
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    )
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* ── Disclosure Footer ── */}
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          borderTop: '1px solid rgba(255,255,255,0.06)',
          bgcolor: '#16171A',
        }}
      >
        <Typography
          sx={{
            fontSize: '0.68rem',
            color: 'rgba(255,255,255,0.5)',
            lineHeight: 1.6,
          }}
        >
          <Box component="span" sx={{ fontWeight: 700, color: 'rgba(255,255,255,0.75)' }}>
            Disclosure:{' '}
          </Box>
          Information provided in this report is for educational purposes only. You should evaluate
          and test your own strategies before risking any money in the market. This report shows
          real accounts from customers and company personnel, both demo and live, connected with our
          Auto-Trader. The Profit % in this report can be affected in many ways by the account
          holder, such as deposits/withdrawals from brokerage account (which adjusts the lot size on
          subsequent trades), manually adjusting S/L and T/P while a trade is open, risk/reward
          settings, and closing trades early.{' '}
          <Box
            component="a"
            href="https://daytradeu.com/disclaimer"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: 'rgba(255,255,255,0.65)',
              textDecoration: 'underline',
              '&:hover': { color: 'rgba(255,255,255,0.9)' },
            }}
          >
            See Full Disclosure.
          </Box>
        </Typography>
      </Box>
    </Box>
  )
}
